import { useCallback, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { EvidenceEntityKind, EvidenceRow } from '@/api/types';
import type { Locale } from '@/i18n/LanguageContext';

function formatBytes(n: number, locale: Locale): string {
  if (n < 1024) return `${formatNumber(n, locale)} B`;
  if (n < 1024 * 1024) return `${formatNumber(n / 1024, locale, { maximumFractionDigits: 1 })} KB`;
  return `${formatNumber(n / (1024 * 1024), locale, { maximumFractionDigits: 1 })} MB`;
}

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C) - seller-scoped photo evidence,
 * reusing the same gallery UI PATTERN apps/ops/src/components/
 * EvidenceGallery.tsx established (ADR-074 UI wiring): lazy-fetched bytes
 * per item on "View", not eagerly for the whole list.
 *
 * Duplicated rather than imported across the app boundary (this epic's own
 * instruction: apps/ops and apps/portal never cross-import) - and
 * genuinely simpler here, not just copy-pasted: the portal API has no
 * evidence UPLOAD route at all (`POST /api/v1/office/evidence` is
 * office-only; a seller can view the evidence Ops attached to their own
 * inbound/return/order, never add their own), so there is no upload form,
 * no RequirePermission wrapper, no EVIDENCE.WRITE concept on this
 * boundary.
 */
export function EvidenceGallery({
  entityKind,
  entityId,
}: {
  readonly entityKind: EvidenceEntityKind;
  readonly entityId: string;
}): JSX.Element {
  const result = useApi(() => endpoints.evidenceList({ entityKind, entityId }), [entityKind, entityId]);
  const [expanded, setExpanded] = useState<Record<string, string>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  const onView = useCallback(
    async (row: EvidenceRow) => {
      if (expanded[row.id]) {
        setExpanded((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        return;
      }
      setLoadingId(row.id);
      try {
        const full = await endpoints.evidenceGet(row.id);
        setExpanded((prev) => ({ ...prev, [row.id]: `data:${full.content_type};base64,${full.bytesBase64}` }));
      } catch {
        // leave collapsed - the row's own metadata stays visible
      } finally {
        setLoadingId(null);
      }
    },
    [expanded],
  );

  if (result.status === 'loading') return <p className="sub">{t('evidence.loading')}</p>;
  if (result.status === 'error') return <p className="sub">{t('evidence.couldNotLoad')}</p>;

  const rows = result.data.evidence;
  if (rows.length === 0) return <p className="sub">{t('evidence.noneUploaded')}</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      {rows.map((row) => (
        <div key={row.id} className="card" style={{ padding: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{statusLabel(t, row.kind)}</div>
          <div className="sub" style={{ fontSize: 11 }}>
            {row.content_type} · {formatBytes(row.size_bytes, locale)}
          </div>
          <div className="sub" style={{ fontSize: 11 }}>
            {formatDateTime(row.created_at, locale)}
          </div>
          <button type="button" onClick={() => onView(row)} disabled={loadingId === row.id} style={{ marginTop: 8, width: '100%' }}>
            {loadingId === row.id ? t('common.loading') : expanded[row.id] ? t('common.hide') : t('evidence.view')}
          </button>
          {expanded[row.id] && (
            <img src={expanded[row.id]} alt={t('evidence.altText', { kind: statusLabel(t, row.kind) })} style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />
          )}
        </div>
      ))}
    </div>
  );
}
